import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import AHDate from "_shared/dates/date";
import AssistantResponseSchema, { AssistantResponseJSON, AssistantResponseParams } from "ai-assistant/assistant-response-schema";
import AssistantAttachment from "ai-assistant/attachments/assistant-attachment";
import AssistantCitation from "ai-assistant/citations/assistant-citation";
import AnswerFeedback from "ai-assistant/feedback/answer-feedback";
import AssistantGrounding from "ai-assistant/grounding/assistant-grounding";
import AssistantResultTable from "ai-assistant/result-tables/assistant-result-table";
import { Role } from "ai-assistant/roles/assistant-role";
import StagedAction from "ai-assistant/staged-actions/staged-action";
import AssistantTurnStep from "ai-assistant/turn-steps/assistant-turn-step";
import { AssistantResponseStatus, carriesAnswerText, isAnswered, isExhausted, isIncomplete, stoppedEarly } from "ai-assistant/statuses/assistant-response-status";

export default class AssistantResponse {
  public static create(values: AssistantResponseParams): AssistantResponse {
    const parsed = AssistantResponseSchema.validate(values);
    if (isNullOrUndefined(parsed)) {
      throw new Error("Invalid AssistantResponse parameters.");
    }
    return new AssistantResponse(parsed);
  }

  public static fromJSON(json: AssistantResponseJSON): AssistantResponse {
    if (isNullOrUndefined(json)) return json;
    const params = AssistantResponseSchema.deserialize(json);
    return new AssistantResponse(params);
  }

  public readonly conversationId: string | undefined;
  public readonly role: Role;
  public readonly text: string;
  public readonly citations: AssistantCitation[];
  public readonly timestamp: AHDate = new AHDate();
  public readonly status: AssistantResponseStatus;
  /** Stable server-owned classification for a failed turn, when one is available. */
  public readonly errorCode: string | undefined;
  /**
   * The trace the server ran this turn under. It identifies the turn's record, so a report
   * about this answer can name exactly which answer it means.
   */
  public readonly traceId: string | undefined;
  /** Work the assistant prepared on this turn, awaiting the user's approval. */
  public readonly stagedActions: StagedAction[];
  /**
   * How much of this answer the server checked against the sources it was given.
   *
   * A reply that said nothing about it - an older API, the user's own turn, an optimistic
   * echo - is NotApplicable rather than Grounded. Reading an absent field as a pass is the
   * mistake the field exists to stop.
   */
  public readonly grounding: AssistantGrounding;
  /**
   * Whether this reply announced work that is not being shown beside it.
   *
   * Set only when a stored conversation is replayed. Prepared actions are deliberately left
   * out of a replayed transcript, but the answers that announced them are replayed word for
   * word - so an answer ending "confirm it below" arrives with nothing below it. The reply
   * says so rather than leaving the reader looking for a control that was never rendered.
   */
  public readonly preparedActionsNotReplayed: boolean;
  /**
   * Whether the reader has since lost a source this replayed answer cited.
   *
   * The answer is shown as it was, because the reader was shown it then. The assistant no longer
   * sends it back to the model, though, so a follow-up cannot build on it - and without saying
   * so, the reader would expect "that figure" to resolve against an answer the assistant can no
   * longer see. See #5211.
   */
  public readonly sourceLost: boolean;
  /** The reader's report on this answer, when one was replayed with the conversation. */
  public readonly feedback: AnswerFeedback | undefined;
  /**
   * Tables the turn's lookups answered with, drawn as cards under the answer.
   *
   * The answer text refers to them rather than repeating their figures (#6223), so they are part
   * of the answer and are drawn on an unfinished turn as well as a finished one.
   */
  public readonly resultTables: AssistantResultTable[];
  /**
   * Whether this replayed answer was shown with a result table that is not replayed.
   *
   * Set only when a stored conversation is replayed. The server does not keep a table with the
   * turn - it was a reading of the records at that moment - but the answer that said "the table
   * below" is replayed word for word, so the reply says why there is nothing below it.
   */
  public readonly resultTablesNotReplayed: boolean;
  /**
   * What the turn did on the way to this answer, in order: the searches and lookups it ran, the
   * check of its citations, and any second pass (#7284). The labels are the server's. None on a
   * failed turn, the user's own message, or an answer recorded before steps were kept.
   */
  public readonly steps: AssistantTurnStep[];
  /** The files the user's own message carried (#6504). None on the assistant's replies. */
  public readonly attachments: AssistantAttachment[];

  /**
   * Whether the reader can report this answer as wrong.
   *
   * Only an answer the server recorded can be reported, and the trace id is how the report names
   * it. A failed turn has a trace id and no recorded answer; a turn from before trace ids were
   * kept has a recorded answer and no way to name it.
   */
  public get isReportable(): boolean {
    return this.role === Role.Assistant
      && this.showsAnswerText
      && !isNullOrUndefined(this.conversationId)
      && !isNullOrUndefined(this.traceId);
  }

  /**
   * Whether this reply's text is an answer rather than the assistant's failure prose.
   *
   * A failed turn arrives as HTTP 200 with no citations, which is exactly what a correct
   * abstention looks like. Without this the two are indistinguishable in the UI.
   */
  public get isAnswer(): boolean {
    return isAnswered(this.status);
  }

  /**
   * Whether the assistant stopped part-way through working this turn out.
   *
   * The text is still shown, with a notice, because it may be most of an answer. What must
   * not appear beside it is a Confirm control: the turn was cut short mid-reasoning, and a
   * write approved on that basis is one the assistant never finished deciding on. The server
   * stages nothing on such a turn, so there is normally nothing to suppress - this is the
   * second lock on the same door.
   */
  public get isIncompleteAnswer(): boolean {
    return isIncomplete(this.status);
  }

  /**
   * Whether the turn was stopped by a limit the server sets rather than by the model.
   *
   * Rendered like an unfinished turn and worded differently. The remedy for running out of
   * steps is a narrower question and the user can act on it; the remedy for running out of
   * budget is not theirs, so suggesting they rephrase would send them round the same loop.
   */
  public get isExhaustedAnswer(): boolean {
    return isExhausted(this.status);
  }

  /** Whether the assistant stopped before finishing, for either reason. */
  public get stoppedBeforeFinishing(): boolean {
    return stoppedEarly(this.status);
  }

  /**
   * Whether this reply's text is content to render rather than an error to report.
   *
   * True for a finished answer and for an unfinished one. False for a failure or a conflict,
   * whose text is prose about the failure and carries nothing to cite.
   */
  public get showsAnswerText(): boolean {
    return carriesAnswerText(this.status);
  }

  /**
   * The prepared actions this reply may offer the user, which is none on an unfinished turn -
   * whether it ran out of steps or out of budget. Either way the reasoning behind the prepared
   * write did not finish.
   */
  public get actionableStagedActions(): StagedAction[] {
    return this.stoppedBeforeFinishing ? [] : this.stagedActions;
  }

  public constructor(params: AssistantResponseParams) {
    this.conversationId = params.conversationId;
    this.role = params.role;
    this.text = params.text;
    // Taken as they are: each was validated where it was decoded, and re-creating them here
    // validated every citation again on every copy - `withStagedActions` makes one for each
    // earlier turn still offering an action whenever a reply arrives (#5992).
    this.citations = [...params.citations];
    this.timestamp = params.timestamp;
    this.status = params.status ?? AssistantResponseStatus.Answered;
    this.errorCode = params.errorCode;
    this.traceId = params.traceId;
    this.stagedActions = (params.stagedActions ?? []).map(action => StagedAction.create(action));
    this.grounding = isNullOrUndefined(params.grounding)
      ? AssistantGrounding.unstated
      : AssistantGrounding.create(params.grounding);
    this.preparedActionsNotReplayed = params.preparedActionsNotReplayed ?? false;
    this.sourceLost = params.sourceLost ?? false;
    this.feedback = params.feedback;
    this.resultTables = [...(params.resultTables ?? [])];
    this.resultTablesNotReplayed = params.resultTablesNotReplayed ?? false;
    this.steps = [...(params.steps ?? [])];
    this.attachments = [...(params.attachments ?? [])];
  }

  /**
   * The same reply with one prepared action redrawn in the state the server has moved it to.
   *
   * A confirm or a cancel is answered with the action it resolved, so the card the user
   * clicked is rewritten where it already sits. Left in `Pending` it would go on offering
   * Confirm and Cancel for work that is already done, which is one click away from a second
   * submission. A reply that never carried this action is returned untouched.
   */
  public withStagedActionResolved(resolved: StagedAction): AssistantResponse {
    if (!this.stagedActions.some(action => action.id.isEqualTo(resolved.id))) return this;

    return this.withStagedActions(
      this.stagedActions.map(action => action.id.isEqualTo(resolved.id) ? resolved : action)
    );
  }

  /**
   * The same reply with any preview still awaiting an answer marked as retired.
   *
   * Applied to every earlier turn when a reply arrives carrying a new preview. The server has
   * already superseded those rows - it does so in the same transaction as the new action, so
   * there is no moment at which two previews in one conversation are both confirmable - but it
   * reports only what this turn staged. Without this the transcript keeps offering Confirm on a
   * card whose only remaining answer is a refusal, which is the double-approval the server
   * spends a transaction preventing.
   *
   * `confirmed` names the actions the user has already sent a *confirmation* for. Those are
   * left alone: the server does not supersede an action it is executing, and only the reply to
   * that confirmation can say what became of the write it may already have performed.
   *
   * A cancellation is not in that set. Cancelling writes no business data, so a cancel whose
   * request never arrived leaves the action exactly as the server left it - superseded, once a
   * newer preview exists - and saying so is more accurate than going on offering Confirm.
   */
  public withPendingStagedActionsSuperseded(
    confirmed: ReadonlySet<string> = new Set<string>()
  ): AssistantResponse {
    const retirable = (action: StagedAction): boolean =>
      action.isPending && !confirmed.has(action.id.value);

    if (!this.stagedActions.some(retirable)) return this;

    return this.withStagedActions(
      this.stagedActions.map(action => retirable(action) ? action.asSuperseded() : action)
    );
  }

  /**
   * The same reply carrying no prepared actions.
   *
   * A resolution reply repeats the action it answered so the transcript can redraw it.
   * Once that has happened, the copy travelling with the reply would render a second card
   * for one action id, so it is dropped rather than shown twice.
   */
  public withoutStagedActions(): AssistantResponse {
    return this.stagedActions.length === 0 ? this : this.withStagedActions([]);
  }

  public toJSON(): AssistantResponseJSON {
    return AssistantResponseSchema.serialize(this);
  }

  public isEqualTo(other: AssistantResponse): boolean {
    return ((!this.conversationId && !other.conversationId) || (this.conversationId === other.conversationId))
      && (
        this.role === other.role
        && this.text === other.text
        && this.timestamp.isEqualTo(other.timestamp)
      );
  }

  public toString(): string {
    return `
      Conversation ID: ${this.conversationId},
      Role: ${this.role},
      Text: ${this.text},
      Citations: ${this.citations.map(citation => citation.toString()).join(", ")},
      Timestamp: ${this.timestamp.toISOString()}
    `;
  }

  /** This reply with a different set of prepared actions and everything else as it was. */
  private withStagedActions(stagedActions: StagedAction[]): AssistantResponse {
    return new AssistantResponse({
      conversationId: this.conversationId,
      role: this.role,
      text: this.text,
      citations: this.citations,
      timestamp: this.timestamp,
      status: this.status,
      errorCode: this.errorCode,
      traceId: this.traceId,
      stagedActions,
      grounding: this.grounding,
      preparedActionsNotReplayed: this.preparedActionsNotReplayed,
      sourceLost: this.sourceLost,
      feedback: this.feedback,
      resultTables: this.resultTables,
      resultTablesNotReplayed: this.resultTablesNotReplayed,
      steps: this.steps,
      attachments: this.attachments,
    });
  }
}
